import React, { useEffect, useMemo } from "react";
import { Paper, Tabs, Tab, Box, Typography } from "@mui/material";
import StoScope from "./SearchTermOperaionTabsContent/STO_Scopes/STO_Scope";
import StoSearchTerms from "./SearchTermOperaionTabsContent/STO_SearchTerms/STO_SearchTerms";
import StoAirRecomondation from "./SearchTermOperaionTabsContent/STO_AiRecomondation/STO_AirRecomondation";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useScopes } from "../../STOperationhooks/useScopes";
import { useSearchTermsTab } from "../../STOperationhooks/useSearchTermsTab";
import ScopesSkeleton from "../../SearchTermSkeletons/ScopesTabSkeleton";
import SearchTermTabskeleton from "../../SearchTermSkeletons/SearchTermTabskeleton";
import { selectUserData } from "../../../../../../features/userSlice";
import { CheckPermissions } from "../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../../../../Page404/Page403";
import { resetScopesTabFiltersAndModals } from "../../../../../../features/scopesTabSlice";
import { useDispatch } from "react-redux";
import {
  resetContradictingSTDefinitionSearchTerm,
  resetIdenticalToExactKeyword,
  resetMissingCrossNegativesSearchTerm,
  resetPerformanceUnclassifiedSearchTerm,
} from "../../../../../../features/searchTermTabSlice";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const SearchTermOperationTabs = () => {
  const dispatch = useDispatch();
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabMap = ["SCOPES", "SEARCH_TERMS", "AI_RECOMMENDATIONS"];

  const hasViewSearchTermTabPermission = CheckPermissions(
    response?.data?.products?.paid_traffic,
    "plan_campaign_ppc",
    "view_st_control"
  );

  // Initialize tab value from URL params. Default to 0 (SCOPES) if not present
  const initialTab = useMemo(() => {
    const tabParam = searchParams.get("tab");
    if (!tabParam) return 0; // Default to SCOPES only if no tab in URL

    const tabIndex = tabMap.findIndex(
      (t) => t.replace(/\s+/g, "_") === tabParam.replace(/\s+/g, "_")
    );
    return tabIndex !== -1 ? tabIndex : 0;
    // eslint-disable-next-line
  }, []); // Only calculate once on mount

  const [value, setValue] = React.useState(initialTab);
  const { fetchScopesTabData } = useScopes(); // Custom hook to fetch scopes data
  const { fetchSearchTermsTabData } = useSearchTermsTab(); // Custom hook to fetch search terms data

  const {
    selectedSTOperationSharedUid,
    selectedSTOperationAccount,
    isScopesTabDataFetched,
    isScopesLoading,
  } = useSelector((state: any) => state.searchTermOperation);

  const { isSearchTermTabDataFetched, searchTermTabLoader } = useSelector(
    (state: any) => state.searchTermTab
  );

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // When user switches tab → update URL param and fetch data if needed
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const currentTab = tabMap[value];
    const urlTab = params.get("tab");

    // Only update URL if tab param is different
    if (urlTab && urlTab !== currentTab) {
      // Get the index of the tab we're LEAVING
      const previousTabIndex = tabMap.findIndex(
        (t) => t.replace(/\s+/g, "_") === urlTab.replace(/\s+/g, "_")
      );

      // Reset filters while switching tabs
      switch (previousTabIndex) {
        case 0: // Leaving SCOPES tab and clear its filters
          dispatch(resetScopesTabFiltersAndModals());
          break;
        case 1: // Leaving SEARCH_TERMS tab clear its filters
          dispatch(resetPerformanceUnclassifiedSearchTerm());
          dispatch(resetMissingCrossNegativesSearchTerm());
          dispatch(resetContradictingSTDefinitionSearchTerm());
          dispatch(resetIdenticalToExactKeyword());
          break;
        case 2: // Leaving AI_RECOMMENDATIONS tab
          // dispatch(resetAiRecommendationsTabFiltersAndModals());
          break;
      }

      // Clean up URL params when switching tabs
      ["filters", "page", "sort", "search", "accordion"].forEach((key) => {
        if (params.has(key)) params.delete(key);
      });
    }

    // Update URL with current tab
    params.set("tab", currentTab);
    setSearchParams(params, { replace: true });

    // Check if we have website and account selected
    const hasRequiredSelections =
      selectedSTOperationSharedUid && selectedSTOperationAccount;

    if (!hasRequiredSelections) return;
    // Fetch data based on the active tab if not already fetched
    switch (currentTab) {
      case "SCOPES":
        if (!isScopesTabDataFetched) {
          fetchScopesTabData(
            selectedSTOperationSharedUid,
            selectedSTOperationAccount
          );
        }
        break;
      case "SEARCH_TERMS":
        if (!isSearchTermTabDataFetched) {
          fetchSearchTermsTabData();
        }
        break;
      case "AI_RECOMMENDATIONS":
        // Add fetch logic for AI recommendations when ready
        console.log("AI Recommendations tab selected");
        break;
      default:
        break;
    }
    // eslint-disable-next-line
  }, [value]);

  // Check if current tab is loading to show skeleton
  const isCurrentTabLoading = () => {
    switch (value) {
      case 0: // SCOPES
        return isScopesLoading && !isScopesTabDataFetched;
      case 1: // SEARCH_TERMS
        return searchTermTabLoader && !isSearchTermTabDataFetched;
      case 2: // AI_RECOMMENDATIONS
        return false; // Add loading check when implemented
      default:
        return false;
    }
  };

  const showSkeleton = isCurrentTabLoading();

  return (
    <Paper
      elevation={0}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* Tabs header */}
      <Tabs
        value={value}
        onChange={handleChange}
        textColor="secondary"
        indicatorColor="secondary"
        sx={{
          "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
        }}
      >
        <Tab label="SCOPES" />
        <Tab label="SEARCH TERMS" />
        <Tab label="AI RECOMMENDATIONS" />
      </Tabs>

      {/* Tab panels */}
      <TabPanel value={value} index={0}>
        {showSkeleton && value === 0 ? <ScopesSkeleton /> : <StoScope />}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {hasViewSearchTermTabPermission ? (
          <>
            {showSkeleton && value === 0 ? (
              <SearchTermTabskeleton />
            ) : (
              <StoSearchTerms />
            )}
          </>
        ) : (
          <Page404 />
        )}
      </TabPanel>
      <TabPanel value={value} index={2}>
        <StoAirRecomondation />{" "}
      </TabPanel>
    </Paper>
  );
};

export default SearchTermOperationTabs;
